import React from 'react'
import { TouchableOpacity } from 'react-native'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { Text } from '@components/base'
import { InlineAlert } from '@src/components/InlineAlert'
import { UiInlineAlert } from '@src/types'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useDataDelayBottomSheet } from '@src/screens/OwnSensorOnboarding/hooks/useDataDelayBottomSheet'
import { useDataStaleBottomSheet } from '@src/screens/OwnSensorOnboarding/hooks/useDataStaleBottomSheet'
import { Storage } from '@src/utils'
// eslint-disable-next-line max-len
import { useShowByosThirdPartyDelaySyncInfo } from '@src/screens/OwnSensorOnboarding/hooks/useShowByosThirdPartyDelaySyncInfo'
import { useGetThirdPartyAppName } from '@src/screens/OwnSensorOnboarding/hooks'
import { dailyChartsSelector } from '@src/models/dailyCharts/selectors'

export const ByosDelaySyncInfo = () => {
  const showPopup = useShowByosThirdPartyDelaySyncInfo()
  const styles = useStyleSheet(themedStyles)
  const showDataDelayBottomSheet = useDataDelayBottomSheet()
  const showDataStaleBottomSheet = useDataStaleBottomSheet()
  const appName = useGetThirdPartyAppName()

  const { primary } = useSelector(dailyChartsSelector)

  const isDataFresh = () => {
    if (!showPopup) {
      return false
    }

    let dataFresh = Storage.get<boolean>(Storage.GLUCOSE_RECENT_KEY)

    // if we have never detected data from today, or never checked, check again
    if (!dataFresh) {
      const glucoseMeasurements = primary.values.filter(
        (value) => value.tag === 'Glucose' && value.y !== null,
      )

      const lastGlucoseMeasurement = glucoseMeasurements[glucoseMeasurements.length - 1] as
        | typeof glucoseMeasurements[0]
        | undefined

      if (!lastGlucoseMeasurement) {
        return dataFresh
      }

      // if we have data from today, set the flag and never check it again
      dataFresh = moment(lastGlucoseMeasurement.x).isSame(moment(), 'day') || null
      if (dataFresh) {
        Storage.set(Storage.GLUCOSE_RECENT_KEY, true)
      }
    }

    return dataFresh
  }

  if (!showPopup) {
    return null
  }

  const dataFresh = isDataFresh()

  const infoMessage = dataFresh
    ? `Reminder, there is a 3 hour delay from the ${appName} app to the Nutrisense app.`
    : 'Have not received data from today.'

  const onPress = () => {
    if (dataFresh) {
      showDataDelayBottomSheet()
    } else {
      showDataStaleBottomSheet()
    }
  }

  return (
    <TouchableOpacity
      accessibilityLabel="BYOS Data Delay"
      activeOpacity={0.7}
      style={styles.wrapperStyle}
      onPress={onPress}
    >
      <InlineAlert style={styles.popup} category={UiInlineAlert.Info} message={infoMessage} />
      <Text type="regular" bold style={styles.textStyle}>
        Learn more
      </Text>
    </TouchableOpacity>
  )
}

const themedStyles = StyleService.create({
  wrapperStyle: {
    justifyContent: 'center',
    marginHorizontal: 16,
    marginTop: 8,
  },
  popup: {
    paddingRight: 96,
  },
  textStyle: {
    position: 'absolute',
    right: 16,
    color: 'theme.text.link',
  },
})
